<script setup>
import { onMounted, reactive, ref, watch } from "vue"
import Swal from "sweetalert2"
import api from "@/api/axios.js"

const DIAS_SEMANA = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"]

const asesoresLideres = ref([])
const ciudades = ref([])
const slotsLibres = ref([])
const cargandoSlots = ref(false)
const guardando = ref(false)

const form = reactive({
  fecha: "",
  slot: "",
  nombreCliente: "",
  celular: "",
  interesadoEn: "",
  asesorLiderId: "",
  ciudadAtencionId: "",
  tipo: "presencial"
})

const diaSemanaTexto = ref("")

watch(() => form.fecha, (fecha) => {
  if (!fecha) {
    diaSemanaTexto.value = ""
    return
  }
  const [year, month, day] = fecha.split("-").map(Number)
  diaSemanaTexto.value = DIAS_SEMANA[new Date(year, month - 1, day).getDay()]
})

async function cargarCatalogosBase() {
  try {
    const [rAsesores, rCiudades] = await Promise.all([
      api.get("/citas/asesores-lideres"),
      api.get("/catalogos/ciudadAtencion")
    ])
    asesoresLideres.value = rAsesores.data.asesoresLideres
    ciudades.value = rCiudades.data.items
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudieron cargar los datos base",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  }
}

onMounted(cargarCatalogosBase)

function alCambiarAsesor() {
  const asesor = asesoresLideres.value.find((a) => a.id === form.asesorLiderId)
  if (asesor?.ciudadAtencionId) form.ciudadAtencionId = asesor.ciudadAtencionId
}

async function cargarDisponibilidad() {
  form.slot = ""
  slotsLibres.value = []
  if (!form.asesorLiderId || !form.fecha) return

  cargandoSlots.value = true
  try {
    const { data } = await api.get("/citas/disponibilidad", {
      params: { asesorLiderId: form.asesorLiderId, fecha: form.fecha }
    })
    slotsLibres.value = data.slotsLibres
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo cargar la disponibilidad",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    cargandoSlots.value = false
  }
}

watch(() => [form.asesorLiderId, form.fecha], cargarDisponibilidad)

async function agendar() {
  if (!form.fecha || !form.slot || !form.nombreCliente || !form.celular || !form.asesorLiderId || !form.ciudadAtencionId) {
    Swal.fire({ icon: "warning", title: "Faltan datos", text: "Completa fecha, horario, cliente, celular, asesor líder y ciudad de atención." })
    return
  }

  const [horaInicio, horaFin] = form.slot.split("|")

  guardando.value = true
  try {
    const { data } = await api.post("/citas", {
      fecha: form.fecha,
      horaInicio,
      horaFin,
      nombreCliente: form.nombreCliente,
      celular: form.celular,
      interesadoEn: form.interesadoEn || null,
      asesorLiderId: form.asesorLiderId,
      ciudadAtencionId: form.ciudadAtencionId || null,
      tipo: form.tipo
    })

    if (data.tipo === "virtual" && data.linkReunion) {
      Swal.fire({
        icon: "success",
        title: "Cita agendada",
        html: `Se generó el link de Google Meet para la reunión:<br><a href="${data.linkReunion}" target="_blank" rel="noopener">${data.linkReunion}</a>`
      })
    } else {
      Swal.fire({ icon: "success", title: "Cita agendada", timer: 1800, showConfirmButton: false })
    }

    Object.assign(form, { fecha: "", slot: "", nombreCliente: "", celular: "", interesadoEn: "", asesorLiderId: "", ciudadAtencionId: "", tipo: "presencial" })
    slotsLibres.value = []
  } catch (err) {
    Swal.fire({
      icon: "error",
      title: "No se pudo agendar la cita",
      text: err.response?.data?.error || "Error de conexión con el servidor."
    })
  } finally {
    guardando.value = false
  }
}
</script>

<template>
  <div class="container-fluid py-4">
    <div class="card shadow-sm">
      <div class="card-header">
        <h5 class="mb-0"><i class="ni ni-calendar-grid-58 text-primary me-2"></i>Agendar Cita</h5>
      </div>
      <div class="card-body">
        <form class="row g-3" @submit.prevent="agendar">
          <div class="col-12">
            <label class="form-label">Tipo de cita *</label>
            <div class="d-flex gap-3">
              <div class="form-check">
                <input class="form-check-input" type="radio" name="tipoCita" id="tipoPresencial" value="presencial" v-model="form.tipo">
                <label class="form-check-label" for="tipoPresencial">Presencial</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="radio" name="tipoCita" id="tipoVirtual" value="virtual" v-model="form.tipo">
                <label class="form-check-label" for="tipoVirtual">Virtual</label>
              </div>
            </div>
            <small v-if="form.tipo === 'virtual'" class="text-muted">
              Al agendar se generará automáticamente un link de Google Meet para la reunión.
            </small>
          </div>
          <div class="col-md-4">
            <label class="form-label">Asesor líder *</label>
            <select class="form-select" v-model="form.asesorLiderId" required @change="alCambiarAsesor">
              <option value="">Selecciona...</option>
              <option v-for="a in asesoresLideres" :key="a.id" :value="a.id">{{ a.name }}</option>
            </select>
          </div>
          <div class="col-md-4">
            <label class="form-label">Fecha *</label>
            <input type="date" class="form-control" v-model="form.fecha" required>
            <small v-if="diaSemanaTexto" class="text-muted">{{ diaSemanaTexto }}</small>
          </div>
          <div class="col-md-4">
            <label class="form-label">Ciudad de atención *</label>
            <select class="form-select" v-model="form.ciudadAtencionId" required>
              <option value="" disabled>Selecciona...</option>
              <option v-for="c in ciudades" :key="c.id" :value="c.id">{{ c.nombre }}</option>
            </select>
          </div>

          <div class="col-12">
            <label class="form-label">Horario disponible *</label>
            <div v-if="cargandoSlots" class="text-muted text-sm">Cargando horarios...</div>
            <div v-else-if="!form.asesorLiderId || !form.fecha" class="text-muted text-sm">
              Selecciona un asesor líder y una fecha para ver los horarios disponibles.
            </div>
            <div v-else-if="slotsLibres.length === 0" class="text-muted text-sm">
              No hay horarios disponibles para esa fecha.
            </div>
            <div v-else class="d-flex flex-wrap gap-2">
              <button
                v-for="s in slotsLibres"
                :key="s.horaInicio"
                type="button"
                class="btn btn-sm"
                :class="form.slot === `${s.horaInicio}|${s.horaFin}` ? 'btn-primary' : 'btn-outline-primary'"
                @click="form.slot = `${s.horaInicio}|${s.horaFin}`"
              >
                {{ s.horaInicio }}
              </button>
            </div>
          </div>

          <div class="col-md-6">
            <label class="form-label">Nombre del cliente *</label>
            <input type="text" class="form-control" v-model="form.nombreCliente" required>
          </div>
          <div class="col-md-6">
            <label class="form-label">Celular *</label>
            <input type="text" class="form-control" v-model="form.celular" required>
          </div>
          <div class="col-12">
            <label class="form-label">¿En qué está interesado?</label>
            <input type="text" class="form-control" v-model="form.interesadoEn" placeholder="Ej: Plan todo incluido Cancún">
          </div>

          <div class="col-12 d-flex justify-content-end">
            <button type="submit" class="btn btn-primary mb-0" :disabled="guardando || !form.slot || !form.ciudadAtencionId">
              {{ guardando ? "Agendando..." : "Agendar cita" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
